import React, { useCallback, useEffect, useState } from "react";
import { campaignsApi, LaunchRequest } from "../../../api/maintenance/campaigns";
import { ts, errMsg } from "./campaignUtils";
import { useNotice } from "../shared/useNotice";

interface Props {
  canApprove: boolean;
  onChanged: () => void;
}

const pill = (s: LaunchRequest["status"]) => (s === "pending" ? "warn" : s === "approved" ? "ok" : s === "rejected" ? "bad" : "muted");

const LaunchRequestsTab: React.FC<Props> = ({ canApprove, onChanged }) => {
  const [rows, setRows] = useState<LaunchRequest[]>([]);
  const [err, setErr] = useNotice("error", "");
  const [msg, setMsg] = useNotice("success", "");
  const [note, setNote] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    try { const r = await campaignsApi.launchRequests({ limit: 100 }); setRows(r.data); }
    catch (e: any) { setErr(errMsg(e, "Failed to load override requests")); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (r: LaunchRequest, approve: boolean) => {
    const n = (note[r.id] || "").trim();
    if (!approve && !n) { setErr("A rejection needs a note."); return; }
    if (!window.confirm(approve
      ? `Approve starting ${r.campaign_name} with ${r.lead_hours_actual}h notice (minimum ${r.lead_hours_required}h)?\n\nThe campaign still has to be scheduled afterwards.`
      : `Reject the override for ${r.campaign_name}?`)) return;
    setBusy(r.id); setErr(""); setMsg("");
    try {
      if (approve) await campaignsApi.approveLaunchRequest(r.id, n || undefined);
      else await campaignsApi.rejectLaunchRequest(r.id, n);
      setMsg(`Request #${r.id} ${approve ? "approved" : "rejected"}.`);
      await load(); onChanged();
    } catch (e: any) { setErr(errMsg(e, "Decision failed")); }
    finally { setBusy(null); }
  };

  const pending = rows.filter((r) => r.status === "pending");
  const past = rows.filter((r) => r.status !== "pending");

  const row = (r: LaunchRequest) => (
    <tr key={r.id}>
      <td><b>{r.campaign_name}</b><div className="sub">#{r.id} · campaign {r.campaign_status}</div></td>
      <td className="sub">{ts(r.requested_starts_at)}</td>
      <td className="num"><span className="ma-pill warn">{r.lead_hours_actual}h</span><div className="sub">{Math.max(0, r.lead_hours_required - r.lead_hours_actual).toFixed(1)}h short of {r.lead_hours_required}h</div></td>
      <td style={{ whiteSpace: "normal", maxWidth: 320, fontSize: 12.5 }}>{r.reason}</td>
      <td className="sub">{r.requested_by}<br />{ts(r.requested_at)}</td>
      <td>
        {r.status === "pending" ? (
          canApprove ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 220 }}>
              <input placeholder="Note (required to reject)" value={note[r.id] || ""} onChange={(e) => setNote({ ...note, [r.id]: e.target.value })}
                style={{ border: "1px solid #e6e8ee", borderRadius: 7, padding: "5px 8px", fontSize: 12 }} />
              <div style={{ display: "flex", gap: 6 }}>
                <button className="ma-btn primary" disabled={busy === r.id} onClick={() => decide(r, true)}>Approve</button>
                <button className="ma-btn" disabled={busy === r.id} onClick={() => decide(r, false)}>Reject</button>
              </div>
            </div>
          ) : <span className="ma-pill warn">awaiting a super-admin</span>
        ) : (
          <>
            <span className={`ma-pill ${pill(r.status)}`}>{r.status}</span>
            {r.self_approved && <span className="ma-pill bad" style={{ marginLeft: 6 }}>self-approved</span>}
            <div className="sub">{r.decided_by ? `${r.decided_by} · ${ts(r.decided_at)}` : ""}{r.decision_note ? ` · "${r.decision_note}"` : ""}</div>
          </>
        )}
      </td>
    </tr>
  );

  return (
    <>
      {err && <div className="ma-error">{err}</div>}
      {msg && <div className="ma-note">{msg}</div>}
      <div className="ma-note">A campaign must start at least the configured lead time after it is scheduled. Twelve hours of notice is what lets the app say "starts tomorrow", lets support answer the first question before it is asked, and leaves room to spot a typo. Skipping it is a decision somebody signs, here.</div>
      <div className="ma-card">
        <div className="ma-card-h"><h3>Override requests</h3><span className="ma-hint">{pending.length} pending</span></div>
        <div className="ma-tablewrap"><table className="ma-table">
          <thead><tr><th>Campaign</th><th>Wants to start</th><th className="num">Notice given</th><th>Reason</th><th>Asked by</th><th>Decision</th></tr></thead>
          <tbody>
            {pending.map(row)}
            {past.length > 0 && <tr><td colSpan={6} style={{ background: "#fafbfc", fontSize: 11, textTransform: "uppercase", letterSpacing: ".4px", color: "#8a92a1", fontWeight: 600 }}>Decided</td></tr>}
            {past.map(row)}
            {!rows.length && <tr><td colSpan={6} className="ma-empty">No override requests</td></tr>}
          </tbody>
        </table></div>
      </div>
      <div className="ma-note" style={{ marginTop: 14 }}>Whoever approves is recorded against it. If the requester and the approver are the same person the row is flagged <b>self-approved</b> and stays that way.</div>
    </>
  );
};

export default LaunchRequestsTab;
